// pages/purchase-entry/StockItemCombobox.tsx

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { StockItemOption } from '@/stores/invoiceEntryStore';

interface StockItemComboboxProps {
  value: string;
  onChange: (value: string | null) => void;
  stockItems: StockItemOption[];
  placeholder?: string;
  className?: string;
}

const MAX_VISIBLE = 50;

export function StockItemCombobox({
  value,
  onChange,
  stockItems,
  placeholder = 'Search item...',
  className,
}: StockItemComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter stock items based on search
  const filteredItems = useMemo(() => {
    if (!search || search.length < 2) return [];
    const query = search.toLowerCase();
    return stockItems
      .filter((item) => item.name.toLowerCase().includes(query))
      .slice(0, MAX_VISIBLE);
  }, [search, stockItems]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (itemName: string | null) => {
    onChange(itemName);
    setSearch('');
    setOpen(false);
  };

  const handleFocus = () => {
    setOpen(true);
    setSearch('');
  };

  const displayValue = open ? search : value;

  return (
    <div ref={containerRef} className={cn('relative', className)} title={value}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
        <input
          type="text"
          value={displayValue}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={handleFocus}
          placeholder={open ? `Search: ${value || placeholder}` : value || placeholder}
          className={cn(
            'w-full h-8 pl-7 pr-6 text-sm rounded-md border border-input bg-background',
            'focus:outline-none focus:ring-1 focus:ring-ring',
            'text-foreground truncate',
            open && !search && 'placeholder:text-foreground/70'
          )}
          title={value}
        />
        <ChevronsUpDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
      </div>

      {open && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
          {/* Option to clear selection */}
          <div
            onClick={() => handleSelect(null)}
            className={cn(
              'flex items-center px-2 py-1.5 text-sm cursor-pointer hover:bg-accent border-b',
              !value && 'bg-accent'
            )}
          >
            <Check
              className={cn(
                'w-3 h-3 mr-2 flex-shrink-0',
                !value ? 'opacity-100' : 'opacity-0'
              )}
            />
            <span className="text-muted-foreground italic">None (Service)</span>
          </div>

          {search.length < 2 ? (
            <div className="p-2 text-sm text-muted-foreground text-center">
              Type 2+ characters to search...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground text-center">
              No items found
            </div>
          ) : (
            <>
              {filteredItems.map((item) => (
                <div
                  key={item.name}
                  onClick={() => handleSelect(item.name)}
                  className={cn(
                    'flex items-center px-2 py-1.5 text-sm cursor-pointer hover:bg-accent',
                    value === item.name && 'bg-accent'
                  )}
                >
                  <Check
                    className={cn(
                      'w-3 h-3 mr-2 flex-shrink-0',
                      value === item.name ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="truncate">{item.name}</span>
                  {(item.group || item.unit) && (
                    <span className="ml-auto text-xs text-muted-foreground truncate max-w-20">
                      {item.group && item.group}
                      {item.unit && ` (${item.unit})`}
                    </span>
                  )}
                </div>
              ))}
              {filteredItems.length === MAX_VISIBLE && (
                <div className="p-1 text-xs text-muted-foreground text-center border-t">
                  Showing first {MAX_VISIBLE} matches...
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
